import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

import { CircleFlag } from 'react-circle-flags'

import i18n from 'i18n-iso-countries'
import ar from 'i18n-iso-countries/langs/ar.json'
import en from 'i18n-iso-countries/langs/en.json'

import { CheckIcon, ChevronDown, Globe } from 'lucide-react'

import { cn } from '@/lib/utils'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

// Register locales
i18n.registerLocale(en)
i18n.registerLocale(ar)

export interface Country {
  alpha2: string
  alpha3: string
  name: string
  countryCallingCodes?: string[]
}

interface CountryDropdownProps {
  onChange?: (country: Country) => void
  defaultValue?: string
  value?: string
  disabled?: boolean
  placeholder?: string
  slim?: boolean
  excludeCountries?: string[]
  onlyCountries?: string[]
  preferredCountries?: string[]
  locale?: string
  className?: string
}

const CountryDropdownComponent = (
  {
    onChange,
    defaultValue,
    value,
    disabled = false,
    placeholder = 'Select a country',
    slim = false,
    excludeCountries = [],
    onlyCountries = [],
    preferredCountries = [],
    locale = 'en',
    className,
    ...props
  }: CountryDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const [open, setOpen] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>()

  // Get translated names
  const translations = useMemo(() => {
    return i18n.getNames(locale) || i18n.getNames('en')
  }, [locale])

  // Build countries list from i18n-iso-countries
  const allCountries: Country[] = useMemo(() => {
    const alpha2Codes = Object.keys(i18n.getAlpha2Codes())
    return alpha2Codes.map((code) => ({
      alpha2: code,
      alpha3: i18n.alpha2ToAlpha3(code) ?? '',
      name: translations[code] ?? code,
    }))
  }, [translations])

  const filteredCountries = useMemo(() => {
    // Normalize user-provided codes to uppercase
    const onlyUpper = onlyCountries.map((c) => c.toUpperCase())
    const excludeUpper = excludeCountries.map((c) => c.toUpperCase())

    let result = [...allCountries]

    if (onlyUpper.length > 0) {
      result = result.filter((c) => onlyUpper.includes(c.alpha2.toUpperCase()))
    }

    if (excludeUpper.length > 0) {
      result = result.filter((c) => !excludeUpper.includes(c.alpha2.toUpperCase()))
    }

    return result
  }, [allCountries, onlyCountries, excludeCountries])

  const [preferred, regular] = useMemo(() => {
    if (preferredCountries.length === 0) return [[], filteredCountries]

    const preferredSet = new Set(preferredCountries)
    const preferredList: Country[] = []
    const regularList: Country[] = []

    filteredCountries.forEach((country) => {
      if (preferredSet.has(country.alpha2)) {
        preferredList.push(country)
      } else {
        regularList.push(country)
      }
    })

    return [preferredList, regularList]
  }, [filteredCountries, preferredCountries])

  // Initialize selected country
  useEffect(() => {
    const targetValue = value ?? defaultValue
    if (targetValue) {
      const initialCountry = filteredCountries.find(
        (country) => country.alpha2.toUpperCase() === targetValue.toUpperCase()
      )
      setSelectedCountry(initialCountry)
    } else {
      setSelectedCountry(undefined)
    }
  }, [value, defaultValue, filteredCountries])

  const handleSelect = useCallback(
    (country: Country) => {
      setSelectedCountry(country)
      onChange?.(country)
      setOpen(false)
    },
    [onChange]
  )

  const triggerClasses = cn(
    'cursor-pointer flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
    slim && 'w-20',
    className
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger ref={ref} className={triggerClasses} disabled={disabled} {...props}>
        {selectedCountry ? (
          <div className='flex w-0 flex-grow items-center gap-2 overflow-hidden'>
            <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full'>
              <CircleFlag countryCode={selectedCountry.alpha2.toLowerCase()} height={20} />
            </div>
            {!slim && <span className='overflow-hidden text-ellipsis whitespace-nowrap'>{selectedCountry.name}</span>}
          </div>
        ) : (
          <span>{slim ? <Globe size={20} /> : placeholder}</span>
        )}
        <ChevronDown size={16} className='text-muted-foreground' />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={10}
        side='bottom'
        className='w-[var(--radix-popper-anchor-width)] p-0'
        align='start'
      >
        <Command className='max-h-[200px] w-full sm:max-h-[270px]'>
          <CommandList className='scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-200'>
            <div className='bg-popover sticky top-0 z-10'>
              <CommandInput placeholder='Search country...' />
            </div>
            <CommandEmpty>No country found.</CommandEmpty>
            {preferred.length > 0 && (
              <>
                <CommandGroup heading='Preferred countries'>
                  {preferred.map((option) => (
                    <CommandItem
                      className='flex w-full cursor-pointer items-center gap-2'
                      key={option.alpha2}
                      onSelect={() => handleSelect(option)}
                    >
                      <div className='flex w-0 flex-grow space-x-2 overflow-hidden'>
                        <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full'>
                          <CircleFlag countryCode={option.alpha2.toLowerCase()} height={20} />
                        </div>
                        <span className='overflow-hidden text-ellipsis whitespace-nowrap'>{option.name}</span>
                      </div>
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4 shrink-0',
                          selectedCountry?.alpha2 === option.alpha2 ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading={preferred.length > 0 ? 'All Countries' : undefined}>
              {regular.map((option) => (
                <CommandItem
                  className='flex w-full items-center gap-2'
                  key={option.alpha2}
                  onSelect={() => handleSelect(option)}
                >
                  <div className='flex w-0 flex-grow space-x-2 overflow-hidden'>
                    <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full'>
                      <CircleFlag countryCode={option.alpha2.toLowerCase()} height={20} />
                    </div>
                    <span className='overflow-hidden text-ellipsis whitespace-nowrap'>{option.name}</span>
                  </div>
                  <CheckIcon
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      selectedCountry?.alpha2 === option.alpha2 ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export const CountryDropdown = forwardRef(CountryDropdownComponent)
